import React from 'react';
import { Link } from 'react-router-dom';
import { Star, MapPin, Heart, BadgeCheck, Crown } from 'lucide-react';
import { listingUrl } from '@/lib/seo';
import { cn } from '@/lib/utils';

const niceCity = (s) => (s || '').replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Active paid Headline sponsorship → gold luxury treatment platform-wide.
export const isGoldPartner = (l) =>
  Boolean(l?.ownership?.homepage) && (l?.ownership?.boost_until || '') > new Date().toISOString();

export const GoldCorners = () => (
  <>
    <span aria-hidden className="gold-corner top-2 left-2 border-t-2 border-l-2 rounded-tl-md" />
    <span aria-hidden className="gold-corner top-2 right-2 border-t-2 border-r-2 rounded-tr-md" />
    <span aria-hidden className="gold-corner bottom-2 left-2 border-b-2 border-l-2 rounded-bl-md" />
    <span aria-hidden className="gold-corner bottom-2 right-2 border-b-2 border-r-2 rounded-br-md" />
  </>
);

export const GoldPartnerBadge = ({ small = false }) => (
  <span
    className={cn(
      'inline-flex items-center gap-1 rounded-full font-bold uppercase tracking-wider text-gray-900',
      'bg-gradient-to-r from-[#f7e08b] via-[#e6c766] to-[#c9a227] shadow-[0_1px_6px_rgba(201,162,39,0.6)]',
      small ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-xs'
    )}
    data-testid="gold-partner-badge"
  >
    <Crown className={small ? 'w-3 h-3' : 'w-3.5 h-3.5'} /> Gold Partner
  </span>
);

/**
 * Unified listing card — used in category pages, city pages, search, related.
 * @param {Object} listing  — server shape from /api/listings
 */
export const ListingCard = ({ listing, compact = false }) => {
  if (!listing) return null;
  const url = listingUrl(listing.type, listing.slug);
  const gold = isGoldPartner(listing);
  const card = (
    <Link
      to={url}
      className={cn(
        'group block bg-white overflow-hidden hover:shadow-xl hover:-translate-y-0.5 transition-all',
        gold ? 'rounded-[14px]' : 'rounded-2xl border border-gray-200'
      )}
      data-testid={`listing-card-${listing.slug || listing.id}`}
    >
      <div className={cn('relative bg-gray-100 overflow-hidden', compact ? 'aspect-[5/3]' : 'aspect-[4/3]')}>
        {listing.image && (
          <img
            src={listing.image}
            alt={listing.title || listing.name}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        )}
        {gold && <GoldCorners />}
        {gold && <span className="gold-shine-sweep" aria-hidden />}
        <div className="absolute top-2.5 left-2.5 flex flex-wrap gap-1.5">
          {gold ? (
            <GoldPartnerBadge small />
          ) : (
            <>
              {listing.premium && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-amber-500 to-yellow-500 text-white text-[10px] font-semibold uppercase tracking-wide">
                  <Crown className="w-3 h-3" /> Premium
                </span>
              )}
              {listing.featured && !listing.premium && (
                <span className="px-2 py-0.5 rounded-full bg-[#D32323] text-white text-[10px] font-semibold uppercase tracking-wide">Featured</span>
              )}
            </>
          )}
          {listing.verified && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/95 text-[#00AA6C] text-[10px] font-semibold">
              <BadgeCheck className="w-3 h-3" /> Verified
            </span>
          )}
        </div>
        <button
          type="button"
          aria-label="Save to favorites"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); /* favorites handled in detail page */ }}
          className="absolute top-2.5 right-2.5 w-8 h-8 rounded-full bg-white/90 backdrop-blur flex items-center justify-center text-gray-700 hover:text-[#D32323] hover:bg-white"
          data-testid={`fav-btn-${listing.slug}`}
        >
          <Heart className="w-4 h-4" />
        </button>
        {listing.distance_km != null && (
          <span className="absolute bottom-2.5 left-2.5 px-2 py-0.5 rounded-full bg-white/95 text-gray-700 text-[10px] font-medium">
            {listing.distance_km} km away
          </span>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-heading font-semibold text-gray-900 group-hover:text-[#00AA6C] transition-colors line-clamp-1">
            {listing.title || listing.name}
          </h3>
          {listing.price && (
            <span className="text-xs text-gray-500 shrink-0">{listing.price}</span>
          )}
        </div>
        <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
          <MapPin className="w-3 h-3" /> <span className="line-clamp-1">{niceCity(listing.city)}</span>
        </div>
        {!compact && (listing.short_description || listing.description) && (
          <p className="text-sm text-gray-600 mt-2 line-clamp-2">{listing.short_description || listing.description}</p>
        )}
        <div className="mt-3 flex items-center justify-between">
          <div className="inline-flex items-center gap-1 text-sm">
            <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
            <strong className="text-gray-900">{listing.rating || '—'}</strong>
            <span className="text-gray-500">({(listing.reviews_count || 0).toLocaleString()})</span>
          </div>
          <span className={cn('text-xs font-medium inline-flex items-center gap-0.5', gold ? 'text-[#8C6B1F]' : 'text-[#00AA6C]')}>View details →</span>
        </div>
      </div>
    </Link>
  );
  if (!gold) return card;
  return (
    <div className="gold-frame rounded-2xl p-[2.5px]" data-testid={`gold-frame-${listing.slug || listing.id}`}>
      {card}
    </div>
  );
};
